import React from 'react';
import { sanitizeCss, sanitizeHtml, sanitizeStyleValue } from '../../Sanitizer.jsx';
import { futureFrameProps } from './templateSectionUtils.jsx';

const contentColorSelectors = {
  eyebrow: 'p:first-child, span:first-child',
  badge: 'span',
  title: 'h1, h2',
  titlePrefix: 'h1, h2',
  titleAccent: 'h1 span, h2 span',
  titleSuffix: 'h1, h2',
  subtitle: 'h1 + p, h2 + p',
  description: 'p',
  quote: 'blockquote, p',
  imageHeading: 'h3, strong',
  imageSubtitle: 'p',
  formTitle: 'form h2, form h3',
  submitLabel: 'button[type="submit"], button',
  buttonText: 'a, button',
  enrollLabel: 'a, button',
  primaryActionLabel: 'a, button',
  secondaryActionLabel: 'a, button',
  actionLabel: 'a, button',
  openLabel: 'span, p',
  closeLabel: 'span, p',
  scheduleTitle: 'h3',
  infoText: 'p',
  priceLabel: 'strong, h3',
  totalLabel: 'strong',
  statusLabel: 'p, span',
  deliveryTitle: 'h3, strong',
  deliveryDescription: 'p',
  categoryTitle: 'h3, strong',
  curriculumTitleTemplate: 'h2, h3, strong',
  email: 'a[href^="mailto"], p, span',
  phone: 'a[href^="tel"], p, span',
  address: 'span, p',
  phoneTitle: 'h3, strong',
  phoneValue: 'p, span',
  emailTitle: 'h3, strong',
  emailValue: 'p, span',
  addressTitle: 'h3, strong',
  addressValue: 'p, span',
  alternateLabel: 'a',
  consentLabel: 'p'
};

const nonTextContentColorKeys = new Set([
  'accentColor',
  'backgroundColor',
  'borderColor',
  'buttonColor',
  'dividerColor',
  'errorColor',
  'glowColor',
  'gridColor',
  'overlayColor',
  'secondaryColor',
  'successColor',
  'surfaceColor',
  'vignetteColor',
  'warningColor'
]);

const collectionColorSelectors = {
  titleColor: 'article:nth-of-type({index}) h1, article:nth-of-type({index}) h2, article:nth-of-type({index}) h3, article:nth-of-type({index}) strong, a:nth-of-type({index}) strong, figure:nth-of-type({index}) figcaption',
  eyebrowColor: 'article:nth-of-type({index}) span:first-child, article:nth-of-type({index}) p:first-child, a:nth-of-type({index}) span:first-child',
  descriptionColor: 'article:nth-of-type({index}) p, a:nth-of-type({index}) p, figure:nth-of-type({index}) blockquote',
  contentsColor: 'article:nth-of-type({index}) li, article:nth-of-type({index}) p',
  labelColor: 'label:nth-of-type({index}), article:nth-of-type({index}) label, article:nth-of-type({index}) span, a:nth-of-type({index})',
  valueColor: 'article:nth-of-type({index}) strong:first-child, article:nth-of-type({index}) h3, article:nth-of-type({index}) span',
  badgeColor: 'article:nth-of-type({index}) span:first-child, a:nth-of-type({index}) span:first-child',
  buttonTextColor: 'article:nth-of-type({index}) a, article:nth-of-type({index}) button, a:nth-of-type({index}), button:nth-of-type({index})',
  textColor: 'article:nth-of-type({index}) a, article:nth-of-type({index}) button, a:nth-of-type({index}), button:nth-of-type({index})',
  priceColor: 'article:nth-of-type({index}) strong, article:nth-of-type({index}) h3',
  dateColor: 'article:nth-of-type({index}) span, article:nth-of-type({index}) p',
  timeColor: 'article:nth-of-type({index}) span, article:nth-of-type({index}) p',
  metaColor: 'article:nth-of-type({index}) small, article:nth-of-type({index}) span, article:nth-of-type({index}) p:last-child',
  quoteColor: 'figure:nth-of-type({index}) blockquote, article:nth-of-type({index}) blockquote, article:nth-of-type({index}) p',
  featuresColor: 'article:nth-of-type({index}) li, article:nth-of-type({index}) p'
};

const fallbackContentColorSelector = (key = '') => {
  const normalized = String(key).toLowerCase();
  if (normalized.includes('title') || normalized.includes('heading')) return 'h1, h2, h3, summary, strong';
  if (normalized.includes('label') || normalized.includes('button') || normalized.includes('action')) return 'a, button, span';
  if (normalized.includes('badge') || normalized.includes('eyebrow') || normalized.includes('kicker')) return 'span, p:first-child';
  if (normalized.includes('quote') || normalized.includes('description') || normalized.includes('subtitle') || normalized.includes('text')) return 'p, blockquote';
  if (normalized.includes('value') || normalized.includes('price') || normalized.includes('total') || normalized.includes('status')) return 'strong, span, p';
  return 'p, span, strong, h1, h2, h3';
};

const fallbackCollectionContentSelector = (key = '') => fallbackContentColorSelector(key)
  .split(',')
  .map((part) => `article:nth-of-type({index}) ${part.trim()}`)
  .join(', ');

const isContentColorValue = (value) => (
  typeof value === 'string'
  && (
    /^#([0-9a-f]{3,8})$/i.test(value.trim())
    || /^rgba?\([^)]+\)$/i.test(value.trim())
    || /^hsla?\([^)]+\)$/i.test(value.trim())
  )
);

const isContentTextStyleValue = (value) => (
  typeof value === 'string'
  && value.trim().length > 0
  && value.trim().length <= 120
  && !/[{};]/.test(value)
  && !/(javascript:|vbscript:|data:text\/html|expression\s*\(|url\s*\(|<\/?style)/i.test(value)
);

const contentTextStyleRules = [
  {
    suffix: 'Color',
    property: 'color',
    isValueAllowed: isContentColorValue,
    isContentKeyAllowed: (contentKey) => !nonTextContentColorKeys.has(`${contentKey}Color`)
  },
  {
    suffix: 'FontFamily',
    property: 'font-family',
    isValueAllowed: isContentTextStyleValue,
    isContentKeyAllowed: (contentKey) => !nonTextContentColorKeys.has(`${contentKey}Color`)
  },
  {
    suffix: 'FontSize',
    property: 'font-size',
    isValueAllowed: isContentTextStyleValue,
    isContentKeyAllowed: (contentKey) => !nonTextContentColorKeys.has(`${contentKey}Color`)
  }
];

const getContentTextStyleRule = (key = '') => contentTextStyleRules.find((rule) => key.endsWith(rule.suffix));

const buildContentColorCss = (section = {}, scopeId = '') => {
  const content = section.content || {};
  const directTextCss = Object.entries(content)
    .filter(([key, value]) => {
      const rule = getContentTextStyleRule(key);
      if (!rule || !rule.isValueAllowed(value)) return false;
      const contentKey = key.slice(0, -rule.suffix.length);
      return rule.isContentKeyAllowed(contentKey);
    })
    .map(([key, value]) => {
      const rule = getContentTextStyleRule(key);
      const contentKey = key.slice(0, -rule.suffix.length);
      const selector = contentColorSelectors[contentKey] || fallbackContentColorSelector(contentKey);
      return selector
        .split(',')
        .map((part) => `#${scopeId} ${part.trim()} { ${rule.property}: ${sanitizeStyleValue(value.trim())} !important; }`)
        .join('\n');
    })
    .join('\n');

  const collectionTextCss = Object.values(content)
    .filter(Array.isArray)
    .flatMap((items) => items.map((item, index) => ({ item, index: index + 1 })))
    .flatMap(({ item, index }) => Object.entries(item || {})
      .filter(([key, value]) => {
        const rule = getContentTextStyleRule(key);
        if (!rule || !rule.isValueAllowed(value)) return false;
        const contentKey = key.slice(0, -rule.suffix.length);
        return rule.isContentKeyAllowed(contentKey);
      })
      .map(([key, value]) => {
        const rule = getContentTextStyleRule(key);
        const contentKey = key.slice(0, -rule.suffix.length);
        return (collectionColorSelectors[`${contentKey}Color`] || fallbackCollectionContentSelector(contentKey))
          .split(',')
          .map((part) => `#${scopeId} ${part.trim().replace(/\{index\}/g, index)} { ${rule.property}: ${sanitizeStyleValue(value.trim())} !important; }`)
          .join('\n');
      }))
    .join('\n');

  return [directTextCss, collectionTextCss].filter(Boolean).join('\n');
};

const scopeCustomCss = (css, scopeId) => {
  return sanitizeCss(css || '').split('}').map((rule) => {
    const parts = rule.split('{');
    if (parts.length < 2) return '';
    const selector = parts[0].trim();
    const body = parts.slice(1).join('{').trim();
    if (!selector || !body || selector.startsWith('@')) return '';
    const scopedSelector = selector.split(',').map((item) => {
      const trimmed = item.trim();
      return trimmed.startsWith(`#${scopeId}`) ? trimmed : `#${scopeId} ${trimmed}`;
    }).join(', ');
    return `${scopedSelector} { ${body} }`;
  }).join('\n');
};

const customPartCode = (section) => {
  const content = section?.content || {};
  const collections = ['blocks', 'highlightBlocks', 'highlights', 'actions', 'stats', 'items', 'images', 'imageGallery', 'scheduleOptions', 'curriculum', 'fields', 'socialLinks', 'categories'];
  return collections.flatMap((key) => (
    Array.isArray(content[key])
      ? content[key].map((item, index) => ({ ...item, collectionKey: key, partIndex: index }))
      : []
  )).filter((item) => item?.customHtml || item?.customCss);
};

export const SectionCustomScope = ({ section, scopeId, children }) => {
  const scopedCss = scopeCustomCss(section.customCss || '', scopeId);
  const parts = customPartCode(section);
  const partCss = parts.map((item) => scopeCustomCss(item.customCss || '', `${scopeId}-part-${item.collectionKey}-${item.partIndex}`)).filter(Boolean).join('\n');
  const contentColorCss = buildContentColorCss(section, scopeId);
  const extraHtml = section.type !== 'customHtml' ? sanitizeHtml(section.customHtml || '') : '';
  const futureProps = futureFrameProps(section);
  const anchorId = section?.content?.anchorId || section?.anchorId;
  const trustBadgeLabel = section.content?.trustBadgeLabel;
  const actionHint = section.content?.actionHint;

  return (
    <div id={scopeId} data-template-section={section.type || 'section'} {...futureProps}>
      {(scopedCss || partCss || contentColorCss) && <style>{`#${scopeId} { isolation: isolate; } ${scopedCss} ${partCss} ${contentColorCss}`}</style>}
      {anchorId && <span id={anchorId} style={{ display: 'block', position: 'relative', top: '-76px', visibility: 'hidden' }} />}
      {children}
      {(trustBadgeLabel || actionHint) && (
        <div style={{ maxWidth: section.style?.maxWidth || '1180px', margin: '0 auto', padding: '0 24px 20px', display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: section.layout?.align === 'center' ? 'center' : 'flex-start' }}>
          {trustBadgeLabel && <span style={{ border: '1px solid currentColor', borderRadius: 999, padding: '6px 12px', fontSize: 12, fontWeight: 800, opacity: .78 }}>{trustBadgeLabel}</span>}
          {actionHint && <span style={{ borderRadius: 999, padding: '6px 12px', fontSize: 12, background: 'rgba(15,23,42,.06)', opacity: .78 }}>{actionHint}</span>}
        </div>
      )}
      {extraHtml && <div dangerouslySetInnerHTML={{ __html: extraHtml }} />}
      {parts.length > 0 && (
        <div style={{ maxWidth: section.style?.maxWidth || '944px', margin: '0 auto', padding: '0 20px 24px' }}>
          {parts.map((item) => {
            const partId = `${scopeId}-part-${item.collectionKey}-${item.partIndex}`;
            const partHtml = sanitizeHtml(item.customHtml || '');
            return partHtml ? <div key={partId} id={partId} dangerouslySetInnerHTML={{ __html: partHtml }} /> : null;
          })}
        </div>
      )}
    </div>
  );
};
